import React, { CSSProperties, useMemo, useState } from "react";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import classNames from "classnames";
import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import { FlatRatePlan } from "@common/schemas/billing.schema";
import { useMobileView } from "@hooks/useMobileView";
import { Button } from "@design-system/Button/Button";
import { BetaTag } from "@design-system/BetaTag/BetaTag";
import { ArrowDownIcon, ArrowUpIcon } from "@icons";
import { useTypedFlags } from "@hooks/useTypedFlags";
import styles from "./Plans.module.scss";
import { PlanHeader } from "./PlanHeader";
import { PlansRow } from "./PlansRow";
import { PlansColumn } from "./PlansColumn";
import {
    detailedFeaturesByPlan,
    detailedFeaturesSections,
    featureDescriptions,
    highlightsPerPlan,
    isBetaPlanFeature,
} from "./plansData";
import { DetailedFeatureSection } from "./DetailedFeatureSection";
import { defaultRenderPrice } from "./defaultRenderPrice";
import { useAdvancedEnterpriseToggle } from "./useAdvancedEnterpriseToggle";

export interface PlansProps {
    plans: FlatRatePlan[];
    stickyHeaders?: boolean;
    renderAction?: (plan: FLAT_RATE_PLANS) => React.ReactNode;
    renderPrice?: (plan: FlatRatePlan) => React.ReactNode;
}

export const Plans: React.FC<PlansProps> = ({
    plans: allPlans,
    stickyHeaders,
    renderAction,
    renderPrice = defaultRenderPrice,
}) => {
    const [expanded, setExpanded] = useState(false);
    const { billingCatalogV3 } = useTypedFlags();
    const { visiblePlans: plans } = useAdvancedEnterpriseToggle(allPlans);

    const isMobile = useMobileView();

    const variables = useMemo(
        () =>
            ({
                "--plans-count": isMobile ? plans.length - 1 : plans.length,
            }) as CSSProperties,
        [plans.length, isMobile],
    );

    let blurredRowsCount = 3;

    if (renderAction) {
        blurredRowsCount += 1;
    }

    return (
        <section
            className={classNames(styles.plans, {
                [styles.expanded]: expanded,
                [styles.stickyHeaders]: stickyHeaders,
            })}
        >
            <div className={styles.grid} style={variables}>
                {!isMobile && (
                    <PlansColumn
                        open={expanded}
                        aboveTheFold={
                            <>
                                <PlansRow
                                    span={blurredRowsCount}
                                    first
                                    sticky
                                />
                                <PlansRow className={styles.sectionTitle}>
                                    {billingCatalogV3
                                        ? "Annual transaction limit"
                                        : "Annual business expense limit"}
                                </PlansRow>
                                <PlansRow className={styles.sectionTitle}>
                                    {billingCatalogV3
                                        ? undefined
                                        : "Key features"}
                                </PlansRow>
                                <PlansRow className={styles.sectionTitle}>
                                    {billingCatalogV3
                                        ? "Key features"
                                        : undefined}
                                </PlansRow>
                                <PlansRow className={styles.sectionTitle}>
                                    Integrations
                                </PlansRow>
                                <PlansRow className={styles.sectionTitle}>
                                    Business entity limit
                                </PlansRow>
                            </>
                        }
                        belowTheFold={detailedFeaturesSections.map(
                            (section) => (
                                <React.Fragment key={section.title}>
                                    <PlansRow
                                        className={classNames(
                                            styles.sectionTitle,
                                            styles.spaceBefore,
                                        )}
                                    >
                                        {section.title}
                                    </PlansRow>

                                    {section.features.map((feature) => {
                                        const description =
                                            featureDescriptions[feature];

                                        return (
                                            <PlansRow
                                                key={feature}
                                                className={styles.featureTitle}
                                            >
                                                <Tooltip content={description}>
                                                    <span>
                                                        {feature}
                                                        {isBetaPlanFeature(
                                                            feature,
                                                        ) ? (
                                                            <BetaTag />
                                                        ) : null}
                                                    </span>
                                                </Tooltip>
                                            </PlansRow>
                                        );
                                    })}
                                </React.Fragment>
                            ),
                        )}
                    />
                )}

                {plans.map((plan) => (
                    <PlansColumn
                        key={plan.plan}
                        open={expanded}
                        aboveTheFold={
                            <PlanHeader
                                plan={plan}
                                highlights={highlightsPerPlan[plan.plan]}
                                action={renderAction?.(plan.plan)}
                                price={renderPrice(plan)}
                            />
                        }
                        belowTheFold={detailedFeaturesSections.map(
                            (section, index) => (
                                <DetailedFeatureSection
                                    key={section.title}
                                    last={
                                        index ===
                                        detailedFeaturesSections.length - 1
                                    }
                                    features={section.features}
                                    featuresForPlan={
                                        detailedFeaturesByPlan[plan.plan]
                                    }
                                />
                            ),
                        )}
                    />
                ))}
            </div>

            {!isMobile && (
                <Button
                    variant="secondary"
                    className={styles.expandButton}
                    onClick={() => setExpanded(!expanded)}
                    data-testid="toggle-plans-details-button"
                    size="xl"
                    rightIcon={expanded ? <ArrowUpIcon /> : <ArrowDownIcon />}
                >
                    {expanded ? "Show less details" : "Compare all features"}
                </Button>
            )}
        </section>
    );
};
